import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import { Link } from 'react-router-dom';
import { Wrench, Droplets, Zap, ArrowRight, Star, MapPin, Phone } from 'lucide-react';
import { motion } from 'framer-motion';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Zap size={40} className="text-orange" />,
      title: 'Klima Servisi',
      description: 'Klima montaj, bakım, arıza onarım, gaz dolumu ve performans artırma hizmetleri.',
      slug: 'klima-servisi',
    },
    {
      icon: <Droplets size={40} className="text-orange" />,
      title: 'Kombi Servisi',
      description: 'Kombi bakım, arıza onarım, parça değişimi ve petek temizliği hizmetleri.',
      slug: 'kombi-servisi',
    },
    {
      icon: <Wrench size={40} className="text-orange" />,
      title: 'Beyaz Eşya Servisi',
      description: 'Çamaşır makinesi, bulaşık makinesi, buzdolabı ve fırın tamir hizmetleri.',
      slug: 'beyaz-esya-servisi',
    },
  ];

  const testimonials = [
    {
      name: 'Mustafa K.',
      rating: 5,
      comment: 'Klima bakımı için geldi. Çok profesyonel ve hızlı hizmet aldık. Teşekkürler!',
    },
    {
      name: 'Zeynep A.',
      rating: 5,
      comment: 'Kombimiz arızalanmıştı, aynı gün gelip hallettiler. Kesinlikle tavsiye ederim.',
    },
    {
      name: 'Ali T.',
      rating: 5,
      comment: 'Çamaşır makinemizi tamir ettiler. Fiyatlar uygun, işçilik kaliteli.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Aden İklimlendirme | İzmir Klima, Kombi ve Beyaz Eşya Teknik Servisi</title>
        <meta
          name="description"
          content="İzmir'de klima, kombi ve beyaz eşya teknik servisi. Profesyonel montaj, bakım ve tamir hizmetleri. Hemen randevu alın!"
        />
        <meta name="keywords" content="Aden İklimlendirme, İzmir genelinde 15 yıllık tecrübeyle klima, kombi ve beyaz eşya teknik servis hizmeti sunar. 7/24 destek, aynı gün servis ve garantili onarım." />
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="hero-gradient min-h-[600px] flex items-center" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-sm uppercase tracking-wider text-orange mb-4 font-semibold">
                İZMİR'İN GÜVENİLİR SERVİSİ
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                Klima, Kombi ve Beyaz Eşya'da <span className="text-orange">Uzman Çözümler</span>
              </h1>
              <p className="text-lg text-slate-200 mb-8 leading-relaxed">
                15 yıllık tecrübeyle 7/24 teknik destek, aynı gün servis ve garantili onarım hizmeti. İzmir genelinde hızlı ve güvenilir çözümler sunuyoruz.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/randevu" data-testid="hero-randevu-button" className="btn-secondary inline-flex items-center justify-center">
                  Hemen Randevu Al
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <a
                  href="tel:+905063853685"
                  data-testid="hero-phone-button"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'conversion', {
                        send_to: 'AW-17944147746/P41VCLrutvYbEKLut-xC'
                      });
                    }
                  }}
                  className="btn-outline !text-white !border-white hover:!bg-slate-100 hover:!text-[#EA580C] hover:!border-[#EA580C] inline-flex items-center justify-center"
                >
                  <Phone className="mr-2" size={20} />
                  +90 506 385 36 85
                </a>

              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden md:block bg-white"
            >
              <img
                src="/adenLogo.png"
                alt="Aden İklimlendirme teknik servis hizmeti"
                className="rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-light" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy mb-4">
              Hizmetlerimiz
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              İzmir'de profesyonel teknik servis hizmetleri ile yanınızdayız.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={`/hizmetler/${service.slug}`} data-testid={`service-card-${service.slug}`}>
                  <div className="service-card bg-white p-8 rounded-lg shadow-sm h-full">
                    <div className="mb-4">{service.icon}</div>
                    <h3 className="text-2xl font-bold text-navy mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed mb-4">{service.description}</p>
                    <div className="flex items-center text-orange font-semibold">
                      Detaylı Bilgi
                      <ArrowRight className="ml-2" size={18} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy mb-4">
              Müşterilerimiz Ne Diyor?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-lg shadow-sm"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="text-orange-500 fill-orange-500" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.comment}"</p>
                <p className="font-semibold text-navy">{testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <MapPin size={48} className="mx-auto mb-6 text-orange" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            İzmir Genelinde Hizmetinizdeyiz
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            İzmir’in tüm ilçelerinde 7/24 profesyonel teknik servis hizmeti sunuyoruz.
          </p>
          <Link to="/randevu" data-testid="cta-randevu-button" className="btn-secondary inline-flex items-center">
            Hemen Randevu Alın
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </>
  );
};

export default HomePage;
